<template lang="pug">
  div
    v-banner(v-if="editedMembers.length > 0" sticky single-line color="success500")
      div.d-flex.justify-space-between
        p.mb-0.h6.white--text Save changes ({{ editedMembers.length }})
        div.d-flex.shrink.justify-end
          v-btn.button-AA-tiny.mr-1(outlined small height="24" color='basic100T08' :disabled="isLoading" @click="clearTask") Cancel
          v-btn.button-AA-tiny.basic800--text(depressed small color="basic100" height="24" :loading="isLoading" @click="saveTask") Save
    div.pa-4
      v-form(ref="form")
        div.px-4.pt-2
          v-text-field.my-2(v-model="searchText" hide-details dense outlined background-color="basic200" height="40" :placeholder="$t('search_member')")
            template(v-slot:append)
                eva-icon(name="search-outline" :fill="$vuetify.theme.themes[$theme].basic600")
        v-divider.my-4
        //- Loading state
        v-layout.py-6(v-if="isFetching")
          v-row.fill-height.ma-0(align='center' justify='center')
            v-progress-circular.ma-auto(:width="2" :size="50" color="primary" indeterminate)
        //- Individual
        div(v-else)
          //- Empty list or search not found
          template(v-if="memberTiers.length === 0")
            div.py-6.d-flex.flex-column.align-center
              v-row.fill-height.ma-0(align='center' justify='center')
                div.mx-auto
                  v-img.mx-auto(:src="require('@/assets/images/icon_empty.png')" style="width:80px; height:80px" contain)
                  p.caption Member not found
          template(v-else v-for="member in memberTiers")
            member-tier-individual-item(ref="individuals" :parentMember="member" :companyTiers="companyTiers" :edited="editedMembers[editedMembers.findIndex(edited => edited.id===member.id)]" :focused="editedMembers.some(edited => edited.id===member.id)" @editValue="updateTask" @removeValue="removeTask")
            v-divider.basic300
        y-pagination-custom.pa-4(v-if="count > 0 && !isFetching" v-model="page" :parentPageSize.sync="pageSize" :count="count" :text="'Show items'")
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import MemberTierIndividualItem from '@/components/resellers/memberTiers/MemberTierIndividualItem.vue'

export default {
  name: 'MemberTiers',
  components: {
    MemberTierIndividualItem
  },
  props: {
    companyTiers: {
      type: Array,
      required: true
    },
    isLoading: {
      type: Boolean
    },
    edited: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      isFetching: false,
      fetchUrl: '/members/',
      memberTiers: [],
      editedMembers: [],
      page: 1,
      pageSize: 20,
      count: 0,
      searchText: '',
      searchCountDown: null
    }
  },
  mounted() {
    this.page = this.$route.query.page ? parseInt(this.$route.query.page) : 1
    this.fetchMemberTiers()
  },
  watch: {
    page(val) {
      history.pushState({}, null, this.$route.path + '?page=' + val)
      if (!this.isFetching) {
        this.fetchMemberTiers()
      }
    },
    pageSize() {
      this.page = 1
      this.fetchMemberTiers()
    },
    searchText: {
      handler() {
        if (this.searchCountDown) {
          clearTimeout(this.searchCountDown)
        }
        this.searchCountDown = setTimeout(() => {
          this.page = 1
          this.fetchMemberTiers()
        }, 200)
      },
      deep: true
    },
    editedMembers(val) {
      this.$emit('update:edited', val.length)
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    ...mapState('user', ['member']),
    filterParams() {
      const params = {
        company: this.company.id,
        ordering: '-id',
        page: this.page,
        page_size: this.pageSize,
        search: this.searchText,
        exclude_roles: 5
      }
      return params
    },
    showModal: {
      get() {
        return this.open
      },
      set(val) {
        return this.$emit('update:open', val)
      }
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchMemberTiers() {
      this.isFetching = true
      this.axios
        .get(this.fetchUrl, { params: this.filterParams })
        .then(resp => {
          this.memberTiers = resp.data.results
          this.count = resp.data.count
          if (resp.data.results.find(member => member.id === this.member.id)) {
            this.memberTiers = resp.data.results.filter(member => member.id !== this.member.id)
            this.count -= 1
          }
          this.isFetching = false
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    removeTask(member) {
      const index = this.editedMembers.findIndex(edited => edited.id === member.id)
      if (index !== -1) {
        this.editedMembers.splice(index, 1)
      }
    },
    updateTask(member) {
      const index = this.editedMembers.findIndex(edited => edited.id === member.id)
      if (index === -1) {
        this.editedMembers.push(member)
      } else {
        this.editedMembers[index].tier = member.tier
      }
    },
    clearTask() {
      this.editedMembers = []
      this.memberTiers = []
      this.fetchMemberTiers()
    },
    saveTask() {
      this.$emit('update:isLoading', true)
      // Prepare url, data
      const url = `/members/tiers`
      const data = { members: this.editedMembers }
      // Post request
      this.$axios
        .post(url, data)
        .then(() => {
          this.clearTask()
          this.showMessage(['success', this.$t('success_message.member_tier_updated')])
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.$emit('update:isLoading', false)
        })
    }
  }
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-primary200);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>

<style lang="scss" scoped>
.scrolling-wrapper {
  overflow-x: scroll;
  overflow-y: hidden;
  white-space: nowrap;
  width: 100%;
  -webkit-overflow-scrolling: touch;
  &::-webkit-scrollbar {
    display: none;
  }
}
</style>